import React from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/styles'
import { useLayoutConstants } from '../hooks/useLayoutConstants'
import { ColumnVersionFull, CellSnapshot } from '../../TPPChangeLog/types'

interface TppComparisonCellProps {
    slotId: string
    comparisonData: ColumnVersionFull
    showContext?: boolean
}

const TppComparisonCell: React.FC<TppComparisonCellProps> = ({ slotId, comparisonData, showContext }) => {
    const layout = useLayoutConstants()
    // Find the cell for this slot from the comparison data
    const comparisonCell = comparisonData.cells.find((cell) => cell.slotId === slotId)
    const theme = useTheme()

    const leftOffset = layout.SCROLL_START + (showContext ? layout.COLUMN_WIDTH : 0)
    const stickyStyle: React.CSSProperties = {
        position: 'sticky',
        left: leftOffset,
        zIndex: 25,
        borderRight: '2px solid',
        borderColor: '#3366FF',
        boxShadow: '4px 0 8px -4px rgba(0,0,0,0.1)',
    }

    const cellBackgroundColor = comparisonCell?.isHighlighted
        ? theme.palette.figma.primary3
        : theme.palette.figma.grayscale.tier7

    const hasContent = !!comparisonCell?.content?.trim()

    return (
        <Stack
            direction="column"
            p={1.5}
            sx={{
                flex: '0 0 auto',
                width: layout.COLUMN_WIDTH,
                bgcolor: '#f5f5f5',
                ...stickyStyle,
            }}
        >
            {comparisonCell ? (
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        height: '100%',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 2,
                        borderRadius: 2,
                        backgroundColor: cellBackgroundColor,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            maxHeight: 350,
                            overflowY: 'auto',
                        }}
                    >
                        <Typography
                            sx={{
                                whiteSpace: 'pre-wrap',
                                minWidth: 0,
                                overflowWrap: 'anywhere',
                                color: hasContent ? 'figma.grayscale.tier2' : 'figma.grayscale.tier4',
                                fontSize: 16,
                                fontWeight: 400,
                                fontStyle: hasContent ? 'normal' : 'italic',
                            }}
                        >
                            {hasContent ? comparisonCell.content : '(empty)'}
                        </Typography>
                    </Box>
                </Paper>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        minHeight: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 2,
                        borderRadius: 2,
                        backgroundColor: theme.palette.figma.grayscale.tier7,
                        opacity: 0.5,
                    }}
                >
                    <Typography
                        sx={{
                            color: 'figma.grayscale.tier4',
                            fontSize: 16,
                            fontWeight: 400,
                            fontStyle: 'italic',
                        }}
                    >
                        No data
                    </Typography>
                </Paper>
            )}
        </Stack>
    )
}

export default TppComparisonCell
